export interface ExportData {
    appSettings?: any;
    layoutData?: any;
    pois: any[];
}

/**
 * MarkdownExporter - Exports structured POI data back to markdown format
 * Generates markdown with HTML comment metadata
 */
export class MarkdownExporter {
    
    /**
     * Export structured data to markdown content
     */
    async export(data: ExportData): Promise<string> {
        let markdown = '';
        
        // Add global header if we have global settings
        if (data.appSettings || data.layoutData) {
            markdown += '---GLOBAL---\n';
            
            // Export layout data
            if (data.layoutData) {
                markdown += this.exportLayoutData(data.layoutData);
            }
            
            // Export app settings
            if (data.appSettings) {
                markdown += this.exportAppSettings(data.appSettings);
            }
            
            markdown += '---\n\n';
        }
        
        // Export POIs
        const poiMarkdowns = data.pois.map(poi => this.exportPOI(poi));
        markdown += poiMarkdowns.join('\n---\n');
        
        return markdown;
    }
    
    /**
     * Export layout data to markdown format
     */
    private exportLayoutData(layoutData: any): string {
        let result = '';
        
        const layoutProps = [
            'layouter', 'gridSpacing', 'gridRows', 'gridCols',
            'gridOffsetX', 'gridOffsetY', 'gridOffsetZ',
            'circleRadius', 'circleOffsetY',
            'spiralRadius', 'spiralHeight', 'spiralTurns',
            'lineSpacing', 'lineDirection', 'rotationStep'
        ];
        
        layoutProps.forEach(prop => {
            if (layoutData[prop] !== undefined) {
                result += `${prop}: ${layoutData[prop]}\n`;
            }
        });
        
        return result;
    }
    
    /**
     * Export app settings to markdown format
     */
    private exportAppSettings(appSettings: any): string {
        let result = '';
        
        const appProps = [
            'cameraDistance', 'cameraHeight', 'cameraRotationSpeed', 'cameraZoomSpeed',
            'billboardFontSize', 'billboardFontFamily', 'billboardBackgroundColor',
            'textBackgroundMode', 'textResolutionFactor',
            'animationSpeed', 'transitionDuration',
            'showDebugInfo', 'showBoundingBoxes', 'showConnectors'
        ];
        
        appProps.forEach(prop => {
            if (appSettings[prop] !== undefined) {
                result += `${prop}: ${appSettings[prop]}\n`;
            }
        });
        
        return result;
    }
    
    /**
     * Export individual POI to markdown format
     */
    private exportPOI(poi: any): string {
        let result = '';
        
        // Add HTML comment with metadata
        const metadata = this.extractPOIMetadata(poi);
        if (Object.keys(metadata).length > 0) {
            result += '<!--\n';
            
            Object.entries(metadata).forEach(([key, value]) => {
                result += `${key}: ${value}\n`;
            });
            
            result += '-->\n';
        }
        
        // Add title
        const title = poi.title || poi.name || 'Untitled POI';
        result += `# ${title}\n`;
        
        // Add main content
        if (poi.mainContent) {
            result += poi.mainContent;
        }
        
        return result;
    }
    
    /**
     * Extract POI metadata (excluding title and mainContent)
     */
    private extractPOIMetadata(poi: any): any {
        const metadata: any = {};
        
        // POI-specific properties
        const poiProps = [
            'scale', 'type', 'uri', 'opacity', 'color',
            'wiggleAmplitude', 'wiggleSpeed', 'rotationSpeed',
            'autoplay', 'loop', 'hideContent'
        ];
        
        poiProps.forEach(prop => {
            if (poi[prop] !== undefined) {
                metadata[prop] = poi[prop];
            }
        });
        
        return metadata;
    }
} 